// src/plugins/autotrack/modules/ClickModule.ts

import { BaseTrackingModule } from './BaseTrackingModule';
import { ClickConfig, RageClickData } from '../types';
import { AutoTrackUtils } from '../utils';

export class ClickModule extends BaseTrackingModule {
  private config: Required<ClickConfig>;
  private rageClickTracker = new Map<
    string,
    { count: number; firstClick: number; lastClick: number }
  >();
  private excludeSelector?: string;
  private includeSelector?: string;

  constructor(config: ClickConfig = {}, excludeSelector?: string, includeSelector?: string) {
    super('Click');

    this.config = {
      enabled: true,
      trackAllClicks: true,
      trackButtonClicks: true,
      trackLinkClicks: true,
      trackImageClicks: true,
      detectRageClicks: true,
      rageClickThreshold: 3,
      rageClickTimeframe: 1000, // 1 second
      captureElementAttributes: ['data-track', 'data-label', 'aria-label'],
      maxTextLength: 100,
      ...config,
    };

    this.excludeSelector = excludeSelector;
    this.includeSelector = includeSelector;
  }

  protected setupTracking(): void {
    this.logger?.debug('ClickModule: Initializing click tracking', this.config);
    if (!this.config.enabled) {
      return;
    }

    // Use capture phase to catch clicks before they bubble
    this.addEventListener(document, 'click', this.handleClick.bind(this), true);

    // Clean up rage click tracker periodically
    setInterval(() => this.cleanupRageClickTracker(), 5000);
  }

  protected cleanup(): void {
    this.rageClickTracker.clear();
  }

  private handleClick = (event: Event): void => {
    const target = event.target as Element;

    if (!target || !this.shouldTrackElement(target)) {
      return;
    }

    const elementInfo = AutoTrackUtils.getElementInfo(
      target,
      this.config.captureElementAttributes,
      this.config.maxTextLength
    );

    // Check for rage clicks
    if (this.config.detectRageClicks) {
      this.checkRageClick(target, event as MouseEvent);
    }

    // Track the click
    this.trackClickEvent(target, elementInfo, event as MouseEvent);
  };

  private shouldTrackElement(element: Element): boolean {
    // Check global exclusion rules
    if (AutoTrackUtils.shouldExcludeElement(element, this.excludeSelector, this.includeSelector)) {
      return false;
    }

    const tagName = element.tagName.toLowerCase();

    // Check specific element type tracking settings
    if (!this.config.trackAllClicks) {
      if (tagName === 'button' && !this.config.trackButtonClicks) return false;
      if (tagName === 'a' && !this.config.trackLinkClicks) return false;
      if (tagName === 'img' && !this.config.trackImageClicks) return false;

      // For other elements, only track if they have click handlers or are interactive
      if (!['button', 'a', 'img', 'input', 'select', 'textarea'].includes(tagName)) {
        const hasClickHandler =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (element as any).onclick !== null ||
          element.getAttribute('onclick') !== null ||
          element.hasAttribute('data-track');

        const isInteractive =
          element.hasAttribute('tabindex') ||
          element.getAttribute('role') === 'button' ||
          element.getAttribute('role') === 'link';

        if (!hasClickHandler && !isInteractive) {
          return false;
        }
      }
    }

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private trackClickEvent(element: Element, elementInfo: any, event: MouseEvent): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clickData: Record<string, any> = {
      ...elementInfo,
      click_x: event.clientX,
      click_y: event.clientY,
      page_x: event.pageX,
      page_y: event.pageY,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      is_visible: AutoTrackUtils.isElementVisible(element),
    };

    // Add link-specific data
    if (element.tagName.toLowerCase() === 'a') {
      const link = element as HTMLAnchorElement;
      clickData.link_url = link.href;
      clickData.link_target = link.target || '_self';
      clickData.is_external = link.hostname !== window.location.hostname;
    }

    // Add button-specific data
    if (element.tagName.toLowerCase() === 'button') {
      const button = element as HTMLButtonElement;
      clickData.button_type = button.type;
      clickData.is_disabled = button.disabled;
    }

    // Add form context if element is within a form
    const form = element.closest('form');
    if (form) {
      clickData.form_id = form.id || undefined;
      clickData.form_name = form.name || undefined;
    }

    this.trackEvent({
      eventType: '$click',
      properties: clickData,
    });
  }

  private checkRageClick(element: Element, event: MouseEvent): void {
    const elementKey = AutoTrackUtils.generateSelector(element);
    const now = Date.now();

    const existing = this.rageClickTracker.get(elementKey);

    if (!existing) {
      this.rageClickTracker.set(elementKey, {
        count: 1,
        firstClick: now,
        lastClick: now,
      });
      return;
    }

    // Check if within timeframe
    if (now - existing.firstClick <= this.config.rageClickTimeframe) {
      existing.count++;
      existing.lastClick = now;

      // Trigger rage click event if threshold reached
      if (existing.count >= this.config.rageClickThreshold) {
        this.trackRageClick(element, existing, event);
        // Reset to prevent multiple rage click events
        this.rageClickTracker.delete(elementKey);
      }
    } else {
      // Reset if outside timeframe
      this.rageClickTracker.set(elementKey, {
        count: 1,
        firstClick: now,
        lastClick: now,
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private trackRageClick(element: Element, clickData: any, event: MouseEvent): void {
    const elementInfo = AutoTrackUtils.getElementInfo(
      element,
      this.config.captureElementAttributes
    );

    const rageClickData: RageClickData = {
      clickCount: clickData.count,
      timeframe: clickData.lastClick - clickData.firstClick,
      element: elementInfo,
      coordinates: { x: event.clientX, y: event.clientY },
    };

    this.trackEvent({
      eventType: '$rage_click',
      properties: {
        ...rageClickData,
        page_url: window.location.href,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      },
    });
  }

  private cleanupRageClickTracker(): void {
    const now = Date.now();
    const timeoutThreshold = this.config.rageClickTimeframe * 2;

    for (const [key, data] of this.rageClickTracker.entries()) {
      if (now - data.lastClick > timeoutThreshold) {
        this.rageClickTracker.delete(key);
      }
    }
  }

  /**
   * Get current rage click statistics
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getRageClickStats(): Record<string, any> {
    return {
      active_trackers: this.rageClickTracker.size,
      threshold: this.config.rageClickThreshold,
      timeframe: this.config.rageClickTimeframe,
    };
  }

  /**
   * Manually track a click event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public trackManualClick(element: Element, additionalData: Record<string, any> = {}): void {
    if (!this.isActive) {
      return;
    }

    const elementInfo = AutoTrackUtils.getElementInfo(
      element,
      this.config.captureElementAttributes
    );

    const clickData = {
      ...elementInfo,
      ...additionalData,
      manual_tracking: true,
      page_url: window.location.href,
    };

    this.trackEvent({
      eventType: '$click',
      properties: clickData,
    });
  }
}
